import { useRef, useState, type KeyboardEvent } from 'react';

interface TokenInputProps {
  id?: string;
  name?: string;
  value: readonly string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export function TokenInput({
  id,
  name,
  value,
  onChange,
  placeholder,
  'aria-label': ariaLabel,
  'aria-labelledby': labelledBy,
}: Readonly<TokenInputProps>) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addValue = () => {
    const nextValue = inputValue.trim();
    if (!nextValue) return;
    if (!value.includes(nextValue)) onChange([...value, nextValue]);
    setInputValue('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addValue();
    } else if (event.key === 'Backspace' && !inputValue && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="combo">
      <div
        className="combo-box"
        onPointerDown={(event) => {
          if ((event.target as HTMLElement).closest('button')) return;
          inputRef.current?.focus();
        }}
      >
        {value.map((item) => (
          <span key={item} className="combo-chip">
            <button
              type="button"
              className="combo-chip-remove"
              aria-label={`Remove ${item}`}
              onClick={() =>
                onChange(value.filter((current) => current !== item))
              }
            >
              ×
            </button>
            <span className="combo-chip-label">{item}</span>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          autoComplete="off"
          className="combo-input"
          aria-label={ariaLabel}
          aria-labelledby={labelledBy}
          placeholder={value.length ? '' : placeholder}
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={onKeyDown}
        />
        {value.length > 0 && (
          <button
            type="button"
            className="combo-clear"
            aria-label="Clear all"
            onClick={() => {
              onChange([]);
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}
